/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useEffect } from 'react';
import mbpLogger from '@module-logger/mbp';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Drawer,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { func, string } from 'prop-types';

import useSalesforceOfferInboxQuery from '../../../../../helpers/Personalization/useSalesforceOfferInboxQuery';
import PersonalizedOfferInbox from './PersonalizedOfferInbox';
import cookies from '../../../../../helpers/common/storage/cookies';
import { getOfferInboxData } from '../../../../../../state/ducks/App/App-Selectors';
import { setOfferInboxDrawerOpen } from '../../../../../../state/ducks/App/App-Actions';
import OfferInboxSkeleton from '../../../GraphqlSkeletonComponents/OfferInboxSkeleton';
import noop from '../../../../../helpers/noop';

const useStyles = makeStyles((theme) => ({
    drawerPaper: {
        padding: 0,
        backgroundColor: '#fff',
    },
    drawerBackdrop: {
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
    drawerContent: {
        padding: '24px',
        width: '414px',
        [theme.breakpoints.down('sm')]: {
            padding: '16px',
            width: '90vw',
        },
    },
}));

const OfferInboxContainer = ({ instanceId }) => {
    const offerInbox = useSelector(getOfferInboxData);
    const dispatch = useDispatch();
    const classes = useStyles();
    const drawerOpen = offerInbox.isDrawerOpen?.[instanceId];

    const handleCloseDrawer = () => {
        dispatch(setOfferInboxDrawerOpen(null));
    };

    if (drawerOpen) {
        return (
            <Drawer
                anchor="right"
                id="offer-inbox-drawer"
                open={drawerOpen}
                onClose={handleCloseDrawer}
                classes={{
                    paper: classes.drawerPaper,
                }}
                BackdropProps={{
                    classes: { root: classes.drawerBackdrop },
                }}
                SlideProps={{
                    direction: 'left',
                }}
            >
                <OfferInboxQuery handleCloseDrawer={handleCloseDrawer} />
            </Drawer>
        );
    }

    return null;
};

OfferInboxContainer.propTypes = {
    instanceId: string.isRequired,
};

const OfferInboxQuery = ({ handleCloseDrawer }) => {
    const persistedUserProfile = cookies.get('persisted_user_profile');
    const classes = useStyles();
    const {
        loading, error, data, variables,
    } = useSalesforceOfferInboxQuery({
        queryName: 'OfferInbox',
        optInHoldoutParticipation: true,
        resolveIdentity: true,
        isSessionHasAccount: !!persistedUserProfile,
    });

    const skip = variables?.skip;
    const hasOffers = data?.offerInbox?.availableOffers?.length > 0;

    useEffect(() => {
        if (error) {
            mbpLogger.logError({
                function: 'OfferInboxContainer',
                message: 'Error loading API data from OfferInbox',
                appName: process.env.npm_package_name,
                module: 'Personalized Offer Inbox',
                jsError: error,
            });
            handleCloseDrawer();
            return;
        }

        if (!loading && !skip && !hasOffers) {
            handleCloseDrawer();
        }
    }, [error, loading, skip, hasOffers, handleCloseDrawer]);

    if (loading || skip) {
        return (
            <Box className={classes.drawerContent} id="offer-inbox-drawer-content">
                <OfferInboxSkeleton />
            </Box>
        );
    }

    if (hasOffers) {
        return (
            <PersonalizedOfferInbox data={data.offerInbox} />
        );
    }

    return null;
};

OfferInboxQuery.propTypes = {
    handleCloseDrawer: func,
};

OfferInboxQuery.defaultProps = {
    handleCloseDrawer: noop,
};

export default OfferInboxContainer;
